import { ref, watch } from 'vue'

// Función para obtener el userId desde localStorage
const getUserIdFromLocalStorage = () => {
  const user = localStorage.getItem('user')
  if (user) {
    return JSON.parse(user).id
  } else {
    return null
  }
}

// Función para cargar datos desde localStorage
export const loadFromLocalStorage = (key, defaultValue = null) => {
  const storedData = localStorage.getItem(key)
  const userId = getUserIdFromLocalStorage()
  if (storedData && userId) {
    const parsedData = JSON.parse(storedData)
    if (parsedData.patients) {
      parsedData.patients = parsedData.patients.filter(
        patient => patient.user_selected === userId
      )
    }
    return parsedData
  }
  return defaultValue
}

// Función para guardar datos en localStorage
export const saveToLocalStorage = (key, data) => {
  localStorage.setItem(key, JSON.stringify(data))
}

// Cargar los datos de pacientes y el último ID seleccionado/agregado desde localStorage
const storeData = ref(
  loadFromLocalStorage('storeData', {
    patients: [],
    lastSelectedOrAddedId: null
  })
)

export function useStore() {
  // Agregar o actualizar datos de un paciente
  const addPatientData = data => {
    if (!data.id) {
      return
    }

    const userId = getUserIdFromLocalStorage()
    if (!userId) {
      return // Salir de la función si no hay usuario en localStorage
    }

    data.user_selected = userId

    // Asegurarse de que storeData.value.patients sea un array
    if (!Array.isArray(storeData.value.patients)) {
      storeData.value.patients = []
    }

    const index = storeData.value.patients.findIndex(
      patient => patient.id === data.id
    )

    if (index !== -1) {
      // Si el paciente existe, actualiza sus datos
      storeData.value.patients[index] = {
        ...storeData.value.patients[index],
        ...data
      }
    } else {
      // Si no existe, agregar al principio de la lista
      storeData.value.patients.unshift(data)
    }

    // Actualizar el último ID agregado o actualizado
    storeData.value.lastSelectedOrAddedId = data.id
  }

  // Obtener datos de un paciente por ID
  const getPatientDataById = id => {
    return storeData.value.patients.find(
      patient => patient.id === (id ?? storeData.value.lastSelectedOrAddedId)
    )
  }

  // Eliminar un paciente por ID
  const removePatientId = id => {
    storeData.value.patients = storeData.value.patients.filter(
      patient => patient.id !== id
    )
    if (storeData.value.lastSelectedOrAddedId === id) {
      storeData.value.lastSelectedOrAddedId = storeData.value.patients.length
        ? storeData.value.patients[0].id
        : null // Ajustar si se elimina el paciente seleccionado
    }
  }

  // Seleccionar un paciente por ID
  const selectPatient = id => {
    storeData.value.lastSelectedOrAddedId = id // Actualizar el último ID seleccionado
  }

  // Mover un paciente de una posición a otra
  const movePatient = (fromIndex, toIndex) => {
    const patient = storeData.value.patients.splice(fromIndex, 1)[0]
    storeData.value.patients.splice(toIndex, 0, patient)
  }

  // Guardar los datos de pacientes y el último ID seleccionado/agregado en localStorage cada vez que cambien
  watch(
    storeData,
    newData => {
      saveToLocalStorage('storeData', newData)
    },
    { deep: true }
  )

  return {
    storeData,
    addPatientData,
    getPatientDataById,
    removePatientId,
    selectPatient,
    movePatient
  }
}
